<template>
  <BaseModal :open="open" size="small" @close="closeModal">
    <template #header> Annotate an event in your data </template>
    <template #default>
      <form
        id="create-annotation-form"
        data-tour="annotation-form"
        data-tour-roomy
        @submit.prevent="submitModal"
      >
        <div>
          <fieldset>
            <legend class="text-sm font-semibold text-gray-700">
              Event duration
            </legend>
            <p class="text-sm text-gray-600">
              {{
                model.eventDurationType === "Multi-day event"
                  ? "How long did this event last?"
                  : "When did this event take place?"
              }}
            </p>
            <div class="mt-4">
              <div class="mb-3 flex items-center">
                <input
                  id="event-duration-single-day"
                  v-model="model.eventDurationType"
                  :value="EventDuration.SINGLE_DAY"
                  type="radio"
                  name="event-duration"
                  class="size-4 text-purple-700 transition duration-150 ease-in-out"
                />
                <label for="event-duration-single-day" class="ml-3 leading-4">
                  <span class="block text-sm font-semibold text-gray-700">
                    Single-day event
                  </span>
                  <p class="text-sm text-gray-600">
                    E.g., the launch of new website
                  </p>
                </label>
              </div>
              <div class="flex items-center">
                <input
                  id="event-duration-multi-day"
                  v-model="model.eventDurationType"
                  :value="EventDuration.MULTI_DAY"
                  type="radio"
                  name="event-duration"
                  class="size-4 text-blue-500 transition duration-150 ease-in-out"
                />
                <label for="event-duration-multi-day" class="ml-3 leading-4">
                  <span class="block text-sm font-semibold text-gray-700">
                    Multi-day event
                  </span>
                  <p class="text-sm text-gray-600">
                    E.g., a week-long TV or radio commercial
                  </p>
                </label>
              </div>
            </div>
          </fieldset>
          <fieldset class="mt-4 flex flex-col space-y-2">
            <div class="w-full">
              <label
                for="event-date"
                class="block text-sm font-semibold text-gray-700"
                >{{
                  model.eventDurationType === EventDuration.MULTI_DAY
                    ? "Start date"
                    : "Event date"
                }}
              </label>
              <DatePicker
                id="event-date"
                v-model="model.startDate"
                class="mt-1 w-full"
              />
            </div>
            <div
              v-if="model.eventDurationType === EventDuration.MULTI_DAY"
              class="w-full"
            >
              <label
                for="end-date"
                class="block text-sm font-semibold text-gray-700"
              >
                End date
              </label>
              <DatePicker
                id="end-date"
                v-model="model.endDate"
                :date-is-invalid="model.endDate < model.startDate"
                date-invalidation-reason="End date cannot be before start date"
                class="mt-1 w-full"
              />
            </div>
          </fieldset>
          <fieldset class="mt-6">
            <Dropdown
              v-model="model.eventType"
              :options="{ defaultLabel: 'Select event type', usesImages: true }"
              :items="dropdownValues"
              label="Event type"
              label-class="block text-sm w-full font-semibold text-gray-700 mb-1"
              dropdown-class="w-full"
            />
          </fieldset>
          <fieldset class="mt-6">
            <label
              for="annotation-content"
              class="mb-1 block text-sm font-semibold text-gray-700"
            >
              Description
            </label>
            <textarea
              id="annotation-content"
              v-model="model.content"
              :rows="3"
              class="mt-1 block w-full rounded-md border-none px-3 py-2 text-sm text-gray-700 shadow-inner ring-1 ring-gray-400 focus:ring-2 focus:ring-blue-300"
              :placeholder="`Tell your coworkers in ${organization.data?.name} a bit more about this event.`"
            />
          </fieldset>
        </div>
      </form>
      <p class="mt-1 text-xs text-gray-700">
        Note: This annotation may be visible to non-Fyr users if shared in a
        dashboard.
      </p>
    </template>
    <template #footer>
      <button type="button" class="btn" @click="closeModal">Cancel</button>
      <button
        data-tour="save-annotation"
        form="create-annotation-form"
        :disabled="!annotationIsValid || createAnnotationMutation.isPending"
        type="submit"
        class="btn-primary ml-1"
      >
        Save
      </button>
    </template>
  </BaseModal>
</template>

<script lang="ts">
import { useManageAnnotationsTracking } from "@/services/gtm";
import { Ref, ref } from "vue";
import { DateTime } from "luxon";
export interface CreateAnnotationModalInstanceType {
  openModal: () => void;
  closeModal: () => void;
  setEventDate: (data: Record<string, string>) => void;
}

export interface UseCreateAnnotationModalReturnType {
  createAnnotationModalRef: Ref<CreateAnnotationModalInstanceType | undefined>;
  onClickCreateAnnotation: (label?: DateTime) => void;
}

export function useCreateAnnotationModal(): UseCreateAnnotationModalReturnType {
  const createAnnotationModalRef = ref<CreateAnnotationModalInstanceType>();
  const gtm = useManageAnnotationsTracking();
  function openCreateAnnotationModal(): void {
    createAnnotationModalRef.value?.openModal();
  }
  function setModalData(data: { label: string }): void {
    createAnnotationModalRef.value?.setEventDate(data);
  }
  function onClickCreateAnnotation(label?: DateTime): void {
    const dateToSet = label ? label.toISO()! : DateTime.local().toISO()!;
    setModalData({ label: dateToSet });
    openCreateAnnotationModal();
    gtm.startCreating();
  }

  return {
    createAnnotationModalRef,
    onClickCreateAnnotation,
  };
}
</script>

<script setup lang="ts">
import { computed, reactive } from "vue";
import BaseModal from "@/components/Modal/BaseModal.vue";
import DatePicker from "@/components/Forms/DatePicker.vue";
import Dropdown from "@/components/Forms/Dropdown.vue";
import {
  UserEventType,
  EventDuration,
  useCreateAnnotationMutation,
} from "@/services/annotation";
import { USER_EVENT_TYPE_ICON } from "@/services/icons";
import { useOrganizationQuery } from "@/services/organization";

const dropdownValues = Object.values(UserEventType).map((t) => ({
  label: t,
  value: t,
  image: USER_EVENT_TYPE_ICON[t],
}));

const organization = useOrganizationQuery();
const open = ref(false);
const model = reactive<{
  startDate: DateTime;
  endDate: DateTime;
  content: string;
  eventType: string;
  eventDurationType: EventDuration;
}>({
  startDate: DateTime.local(),
  endDate: DateTime.local(),
  content: "",
  eventType: "",
  eventDurationType: EventDuration.SINGLE_DAY,
});

const annotationIsValid = computed(() => {
  return (
    model.startDate !== null &&
    (model.eventDurationType === EventDuration.SINGLE_DAY ||
      model.endDate > model.startDate) &&
    model.content !== "" &&
    model.eventType !== ""
  );
});

function resetModelData(): void {
  model.content = "";
  model.eventDurationType = EventDuration.SINGLE_DAY;
  model.eventType = "";
}
function openModal(): void {
  open.value = true;
}
function closeModal(): void {
  open.value = false;
}

function setEventDate(data: { label: string }): void {
  resetModelData();
  model.startDate = DateTime.fromISO(data.label);
  model.endDate = DateTime.fromISO(data.label);
}

defineExpose({ openModal, closeModal, setEventDate });

const createAnnotationMutation = useCreateAnnotationMutation();
const gtm = useManageAnnotationsTracking();

function submitModal(): void {
  // TODO: "Continuous" event -> End date = null?
  let endDate = model.endDate.toISODate();
  if (model.eventDurationType === EventDuration.SINGLE_DAY) {
    endDate = model.startDate.toISODate();
  }
  if (annotationIsValid.value) {
    // TODO: Change the parameters to be for a "created annotation" event

    createAnnotationMutation
      .mutateAsync({
        start: model.startDate.toISODate()!,
        end: endDate,
        type: model.eventType as UserEventType,
        description: model.content,
      })
      .then(() => {
        gtm.saveCreate("Success - " + String(model.eventType));
        closeModal();
      })
      .catch((err) => {
        gtm.saveCreate("́Error");
        // TODO: Error handling here - Highlight faulty fields
        throw err;
      });
  }
}
</script>
